/* eslint-disable no-underscore-dangle */
import { useState } from "react";
import { trim } from "lodash";
import PropTypes from "prop-types";
import AddressCard from "@shared/AddressCard";
import IconButton from "@shared/v2/IconButton";
import Button from "@shared/v2/Button";
import { PencilSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";

import AddEditModalBody from "../../../PersonDetail/components/AddEditModalBody";

import { getSuggestedAddressCheck } from "../../../PersonDetail/shared/api";
import InvalidAddressModalBody from "../../../PersonDetail/components/InvalidAddressModal/InvalidAddressModalBody";
import VerifyAddressModalBody from "../../../PersonDetail/components/VerifyAddressModal/VerifyAddressModalBody";

function AddressView({ currentAddress, setPersonValue, options, addressList }) {
  const {
    addressType,
    city,
    postalCode,
    streetAddress,
    locality,
    initShowAddressForm,
    country,
    secondaryAddress,
  } = currentAddress;
  const [showAddressForm, setShowAddressForm] = useState(initShowAddressForm);
  const [showAddressCard, setShowAddressCard] = useState(!initShowAddressForm);
  const [showInvalidBanner, setShowInvalidBanner] = useState(null);

  const [showVerifyAddress, setShowVerifyAddress] = useState(null);
  const [isSuggestedAddress, setIsSuggestedAddress] = useState(true);
  const [initAddress] = useState(currentAddress);

  const [initLoading, setInitLoading] = useState(false);

  const onRemove = (targetAddress) => {
    let filteredAddressList = addressList;
    if (Object.hasOwnProperty.call(targetAddress, "id")) {
      filteredAddressList = addressList.map((address) =>
        address === targetAddress ? { ...address, _destroy: true } : address,
      );
    } else {
      filteredAddressList = addressList.filter((address) => address !== targetAddress);
    }

    setPersonValue({ key: "addressesAttributes", value: filteredAddressList });
  };

  const updateAddress = (targetAddress, value) =>
    setPersonValue({
      key: "addressesAttributes",
      value: addressList.map((address) => {
        if (address !== targetAddress) return address;
        return { ...address, ...value };
      }),
    });

  return currentAddress._destroy ? null : (
    <div className="tw-border-1px tw-border-solid tw-border-gray-10 tw-border-l-0 tw-border-t-0 tw-border-r-0 tw-pb-24px">
      {showAddressCard && (
        <div className="tw-flex tw-justify-between tw-items-center">
          <AddressCard
            addressType={addressType}
            streetAddress={streetAddress}
            cityStateZip={`${city}, ${locality} ${postalCode}`}
            standardized={false}
            secondaryAddress={secondaryAddress}
          />
          <div className="tw-flex tw-gap-8px">
            <IconButton
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowAddressCard(false);
              }}
            >
              <PencilSolidV6 />
            </IconButton>
            <IconButton
              schema="tertiary"
              onClick={() => {
                onRemove(currentAddress);
              }}
            >
              <TrashCanSolidV6 />
            </IconButton>
          </div>
        </div>
      )}
      {showAddressForm && (
        <div>
          <AddEditModalBody
            addressTypes={options}
            setAddressType={(newValue) => updateAddress(currentAddress, { addressType: newValue })}
            addressType={addressType}
            setStreetAddress={(newValue) => updateAddress(currentAddress, { streetAddress: newValue })}
            streetAddress={streetAddress}
            secondaryAddress={secondaryAddress}
            setSecondaryAddress={(newValue) => updateAddress(currentAddress, { secondaryAddress: newValue })}
            city={city}
            setCity={(newValue) => updateAddress(currentAddress, { city: newValue })}
            setLocality={(newValue) => updateAddress(currentAddress, { locality: newValue })}
            locality={locality}
            setPostalCode={(newValue) => updateAddress(currentAddress, { postalCode: newValue })}
            postalCode={postalCode}
            country={country}
            setCountry={(ct) => updateAddress(currentAddress, { country: ct })}
            updateCompleteAddress={(completedAddress) => updateAddress(currentAddress, completedAddress)}
            standardized
          />
          <div className="tw-flex tw-justify-end tw-mt-32px">
            <div className="tw-flex tw-gap-8px">
              <Button
                schema="tertiary"
                onClick={() => {
                  // If this is the initial address form, remove the address from the list
                  if (initShowAddressForm) {
                    setPersonValue({
                      key: "addressesAttributes",
                      value: addressList.filter((address) => address.tempId !== currentAddress.tempId),
                    });
                    return;
                  }

                  // Else, resets to prior address state based on address card
                  setPersonValue({
                    key: "addressesAttributes",
                    value: addressList.map((address) => {
                      if (address.id !== currentAddress.id || address.tempId !== currentAddress.tempId)
                        return address;

                      return initAddress;
                    }),
                  });
                  setShowAddressForm(false);
                  setShowAddressCard(true);
                }}
              >
                Discard
              </Button>
              <Button
                schema="primary"
                isLoading={initLoading}
                onClick={async () => {
                  setInitLoading(true);
                  const { res: resGeoCode } = await getSuggestedAddressCheck({
                    addressType,
                    city,
                    postalCode,
                    streetAddress,
                    secondaryAddress,
                    locality,
                    country,
                  });
                  setInitLoading(false);
                  if (!resGeoCode.data?.addressDetails?.standardized) {
                    if (trim(resGeoCode.data?.addressDetails?.streetAddress) === "") {
                      setShowInvalidBanner({
                        city,
                        postalCode,
                        streetAddress,
                        secondaryAddress,
                        locality,
                        country,
                      });
                    } else {
                      setShowVerifyAddress({
                        originalAddress: {
                          city,
                          postalCode,
                          streetAddress,
                          secondaryAddress,
                          locality,
                          country,
                        },
                        suggestedAddress: resGeoCode.data?.addressDetails,
                      });
                    }
                    setShowAddressForm(false);
                    return;
                  }

                  setShowAddressForm(false);
                  setShowAddressCard(true);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
      {showInvalidBanner && (
        <div className="tw-flex tw-flex-col tw-gap-[24px]">
          <InvalidAddressModalBody
            streetAddress={showInvalidBanner.streetAddress}
            secondaryAddress={showInvalidBanner.secondaryAddress}
            city={showInvalidBanner.city}
            locality={showInvalidBanner.locality}
            postalCode={showInvalidBanner.postalCode}
            country={showInvalidBanner.country}
          />
          <div className="tw-flex tw-gap-8px tw-justify-end">
            <Button
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowInvalidBanner(false);
              }}
            >
              Edit Address
            </Button>
            <Button
              schema="primary"
              onClick={() => {
                setShowAddressForm(false);
                setShowInvalidBanner(false);
                setShowAddressCard(true);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      )}
      {showVerifyAddress && (
        <>
          <VerifyAddressModalBody
            suggestedAddress={showVerifyAddress.suggestedAddress}
            originalAddress={showVerifyAddress.originalAddress}
            isSuggestedAddress={isSuggestedAddress}
            setIsSuggestedAddress={setIsSuggestedAddress}
          />
          <div className="tw-flex tw-gap-8px tw-justify-end tw-mt-16px">
            <Button
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowVerifyAddress(null);
              }}
            >
              Edit Address
            </Button>
            <Button
              schema="primary"
              onClick={() => {
                updateAddress(
                  currentAddress,
                  isSuggestedAddress ? showVerifyAddress.suggestedAddress : showVerifyAddress.originalAddress,
                );
                setShowAddressForm(true);
                setShowVerifyAddress(null);
              }}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
const addressPropType = PropTypes.shape({
  addressType: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  locality: PropTypes.string,
  secondaryAddress: PropTypes.string,
  streetAddress: PropTypes.string,
  postalCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.number,
  initShowAddressForm: PropTypes.bool,
  tempId: PropTypes.string,
  _destroy: PropTypes.bool,
});
AddressView.propTypes = {
  currentAddress: addressPropType,
  setPersonValue: PropTypes.func.isRequired,
  addressList: PropTypes.arrayOf(addressPropType),
  options: PropTypes.arrayOf(PropTypes.shape({})),
};

AddressView.defaultProps = {
  currentAddress: null,
  addressList: [],
  options: [],
};
export default AddressView;
